import {
  useState, useEffect,
  useRef,
} from 'react';
import {
  Box, Button, CircularProgress, Typography,
} from '@mui/material';
import { resendToken } from '../../api/tokens/resend-token';
import { useStatusMessageDispatch } from '../../hooks/StatusMessageContext';
import { isApiError } from '../../utils/errors';
import { EmailConfirmationMessageProps } from '../../types';
import { useTranslation } from 'react-i18next';

export const LAST_EMAIL_SENT_KEY = 'last-email-sent';
const COUNTDOWN_TIME_IN_MS = 30000;
const ONE_SECOND_IN_MS = 1000;

export const setEmailSentTimestamp = (publicId: string) => {
  const storageKey = `${LAST_EMAIL_SENT_KEY}-${publicId}`;
  localStorage.setItem(storageKey, Date.now().toString());
};

export const EmailConfirmationMessage = ({
  slug,
  publicId,
}: EmailConfirmationMessageProps) => {
  const { setStatusMessage } = useStatusMessageDispatch();
  const [ isResending, setIsResending ] = useState(false);
  const { t } = useTranslation('components', { keyPrefix: 'email-confirmation-message' });

  // Check if we should start with a countdown based on localStorage
  const getInitialCountdown = () => {
    const storageKey = `${LAST_EMAIL_SENT_KEY}-${publicId}`;
    const lastSentTimestamp = localStorage.getItem(storageKey);

    if (lastSentTimestamp) {
      const timeElapsed = Date.now() - parseInt(lastSentTimestamp, 10);
      const remainingTime = Math.max(0, COUNTDOWN_TIME_IN_MS - timeElapsed);
      return Math.ceil(remainingTime / ONE_SECOND_IN_MS) * ONE_SECOND_IN_MS;
    }

    return 0; // No previous email sent, start with 0
  };

  const [ countdown, setCountdown ] = useState(getInitialCountdown);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Single effect that runs once and manages the interval
  useEffect(() => {
    // Start the interval that runs every second
    intervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        // Only decrement if countdown > 0
        if (prev > 0) {
          return prev - ONE_SECOND_IN_MS;
        }
        return prev; // Keep at 0 if already 0
      });
    }, ONE_SECOND_IN_MS);

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  const handleResendClick = async () => {
    if (!publicId || !slug || isResending || countdown > 0) return;
    setIsResending(true);
    try {
      await resendToken(slug, publicId);
      setStatusMessage({
        severity: 'success',
        message: t('resend_successful'),
      });

      // Store timestamp in localStorage and start 30-second countdown
      setEmailSentTimestamp(publicId);
      setCountdown(COUNTDOWN_TIME_IN_MS);
    } catch (error) {
      if (isApiError(error)) {
        setStatusMessage({
          severity: 'error',
          message: error.message || t('resend_failed'),
        });
      }
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        flexGrow: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: 5,
      }}
    >
      <Box
        sx={{
          padding: '2rem 3rem',
          textAlign: 'center',
        }}
      >
        <Typography
          fontSize='1.4rem'
          sx={{ mb: 2 }}
          variant='subtitle1'
        >
          {t('confirm_email_message')}
        </Typography>
        <Button
          color='primary'
          disabled={isResending || countdown > 0}
          sx={{
            padding: '4px 12px',
            minWidth: 'auto',
            whiteSpace: 'nowrap',
            lineHeight: 2.2,
          }}
          variant='contained'
          onClick={handleResendClick}
        >
          {/* Left placeholder to balance spinner width */}
          <Box sx={{
            width: 18,
            height: 18,
            mr: 1,
          }}
          >
            {isResending
              ? (
                <Box sx={{
                  width: 16,
                  height: 16,
                  visibility: 'hidden',
                }}
                />
                )
              : null}
          </Box>

          {countdown > 0 ? t('email_sent') : t('resend')}

          {/* Right spinner */}
          <Box sx={{
            width: 18,
            height: 18,
            ml: 1,
          }}
          >
            {isResending && (
              <CircularProgress
                color='inherit'
                size={16}
              />
            )}
          </Box>
        </Button>

        {countdown > 0 && (
          <Typography
            sx={{
              mt: 1,
              color: 'text.secondary',
              fontSize: '0.875rem',
            }}
            variant='body2'
          >
            {t('resend_email_text', { count: countdown / ONE_SECOND_IN_MS })}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// Add displayName to prevent minification issues in production
EmailConfirmationMessage.displayName = '';
